import React, { useEffect, useState } from "react";
import { Button, message, Modal, Select, Tag } from "antd";
import {
  ArrowLeftOutlined,
  PlusOutlined,
  CreditCardOutlined,
  TeamOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import GenericTable from "../../../../Components/Tables/GenericTable";
import {
  generateFuelCards,
  getFuelCards,
  getQRFleetCompanies,
} from "../../../../API/Admin/QRManagement";
import "./style.css";
import usePageAccess from "../../../../Utlis/usePageAccess";

const getErrorMessage = (data, fallback) =>
  data?.message || data?.detail || fallback;

const maskCardNumber = (cardNumber) =>
  cardNumber ? `**** **** **** ${cardNumber.slice(-4)}` : "-";

const capitalize = (str) =>
  str ? str.charAt(0).toUpperCase() + str.slice(1) : str;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const formatLimit = (value, enabled) =>
  enabled && value != null ? `$${parseFloat(value).toFixed(2)}` : "-";

const parseCurrency = (formatted) =>
  parseFloat((formatted || "").replace(/[^0-9.-]/g, "")) || -1;

const mapFuelCard = (item) => ({
  key: String(item.id),
  cardNumber: maskCardNumber(item.card_number),
  status: capitalize(item.status) || "-",
  expiryDate: formatDate(item.expiry_date),
  generatedAt: formatDate(item.generated_at),
  generatedBy: item.generated_by_name || "-",
  dailyLimit: formatLimit(item.daily_limit, item.daily_limit_enabled),
  weeklyLimit: formatLimit(item.weekly_limit, item.weekly_limit_enabled),
  monthlyLimit: formatLimit(item.monthly_limit, item.monthly_limit_enabled),
  weekendLimit: formatLimit(item.weekend_limit, item.weekend_limit_enabled),
  allowAnyDriver: item.allow_any_driver,
  autoReset: item.auto_reset,
});

const FuelCards = () => {
  const { canEdit } = usePageAccess("qr-management");
  const navigate = useNavigate();
  const [cards, setCards] = useState([]);
  const [generateModalOpen, setGenerateModalOpen] = useState(false);
  const [generateCompanyId, setGenerateCompanyId] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState(null);

  const fetchFuelCards = async (fleetCompanyId) => {
    try {
      const res = await getFuelCards(fleetCompanyId);

      if (Math.floor(res?.status / 100) === 2) {
        const list = Array.isArray(res?.data?.data) ? res.data.data : [];
        setCards(list.map(mapFuelCard));
      } else {
        message.error(getErrorMessage(res?.data, "Failed to load fuel cards."));
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load fuel cards."),
      );
    }
  };

  useEffect(() => {
    fetchFuelCards(selectedCompany);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  const fetchCompanies = async () => {
    setCompaniesLoading(true);
    try {
      const res = await getQRFleetCompanies();
      const list = res?.data?.data || [];

      if (Math.floor(res?.status / 100) === 2 && Array.isArray(list)) {
        setCompanies(list);
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to load fleet companies."),
      );
    } finally {
      setCompaniesLoading(false);
    }
  };

  useEffect(() => {
    fetchCompanies();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const companyOptions = companies.map((company) => ({
    value: company.id,
    label: company.legal_name || "-",
  }));

  const generateCompany = companies.find(
    (company) => company.id === generateCompanyId,
  );
  const cardsToGenerate = Number(generateCompany?.cards_to_generate) || 0;

  const closeGenerateModal = () => {
    setGenerateModalOpen(false);
    setGenerateCompanyId(null);
  };

  const handleGenerateCards = async () => {
    if (!generateCompanyId) {
      message.error("Please select a fleet manager.");
      return;
    }

    setGenerating(true);
    try {
      const res = await generateFuelCards(generateCompanyId);

      if (Math.floor(res?.status / 100) === 2) {
        message.success(res?.data?.message || "Fuel cards generated.");
        closeGenerateModal();
        fetchFuelCards(selectedCompany);
        fetchCompanies();
      } else {
        message.error(
          getErrorMessage(res?.data, "Failed to generate fuel cards."),
        );
      }
    } catch (err) {
      message.error(
        getErrorMessage(err?.response?.data, "Failed to generate fuel cards."),
      );
    } finally {
      setGenerating(false);
    }
  };

  const statusFilters = [...new Set(cards.map((item) => item.status))].map(
    (status) => ({ text: status, value: status }),
  );

  const generatedByFilters = [
    ...new Set(cards.map((item) => item.generatedBy)),
  ].map((name) => ({ text: name, value: name }));

  const yesNoFilters = [
    { text: "Yes", value: true },
    { text: "No", value: false },
  ];

  const columns = [
    {
      title: "Card Number",
      dataIndex: "cardNumber",
      key: "cardNumber",
      enableSearch: true,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      filters: statusFilters,
      onFilter: (value, record) => record.status === value,
      render: (status) => (
        <span
          className={`fuel-cards-page__status-badge fuel-cards-page__status-badge--${(status || "").toLowerCase()}`}
        >
          {status}
        </span>
      ),
    },
    {
      title: "Generated At",
      dataIndex: "generatedAt",
      key: "generatedAt",
      sorter: (a, b) =>
        new Date(a.generatedAt).getTime() - new Date(b.generatedAt).getTime(),
    },
    {
      title: "Generated By",
      dataIndex: "generatedBy",
      key: "generatedBy",
      enableSearch: true,
      filters: generatedByFilters,
      onFilter: (value, record) => record.generatedBy === value,
    },
    {
      title: "Expiry Date",
      dataIndex: "expiryDate",
      key: "expiryDate",
      sorter: (a, b) =>
        new Date(a.expiryDate).getTime() - new Date(b.expiryDate).getTime(),
    },
    {
      title: "Daily Limit",
      dataIndex: "dailyLimit",
      key: "dailyLimit",
      sorter: (a, b) =>
        parseCurrency(a.dailyLimit) - parseCurrency(b.dailyLimit),
    },
    {
      title: "Weekly Limit",
      dataIndex: "weeklyLimit",
      key: "weeklyLimit",
      sorter: (a, b) =>
        parseCurrency(a.weeklyLimit) - parseCurrency(b.weeklyLimit),
    },
    {
      title: "Monthly Limit",
      dataIndex: "monthlyLimit",
      key: "monthlyLimit",
      sorter: (a, b) =>
        parseCurrency(a.monthlyLimit) - parseCurrency(b.monthlyLimit),
    },
    {
      title: "Weekend Limit",
      dataIndex: "weekendLimit",
      key: "weekendLimit",
      sorter: (a, b) =>
        parseCurrency(a.weekendLimit) - parseCurrency(b.weekendLimit),
    },
    {
      title: "Allow Any Driver",
      dataIndex: "allowAnyDriver",
      key: "allowAnyDriver",
      filters: yesNoFilters,
      onFilter: (value, record) => record.allowAnyDriver === value,
      render: (val) => (
        <Tag color={val ? "success" : "default"}>{val ? "Yes" : "No"}</Tag>
      ),
    },
    {
      title: "Auto Reset",
      dataIndex: "autoReset",
      key: "autoReset",
      filters: yesNoFilters,
      onFilter: (value, record) => record.autoReset === value,
      render: (val) => (
        <Tag color={val ? "success" : "default"}>{val ? "Yes" : "No"}</Tag>
      ),
    },
  ];

  return (
    <div className="fuel-cards-page">
      <div className="fuel-cards-page__header-row">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
          Back
        </Button>
        {canEdit && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setGenerateModalOpen(true)}
          >
            Generate Fuel Cards
          </Button>
        )}
      </div>

      <div className="fuel-cards-page__filter">
        <div className="fuel-cards-page__filter-text">
          <span className="fuel-cards-page__filter-title">
            Filter by Fleet Manager
          </span>
          <span className="fuel-cards-page__filter-hint">
            Show fuel cards for a single fleet
          </span>
        </div>
        <Select
          prefix={<TeamOutlined className="fuel-cards-page__filter-icon" />}
          allowClear
          showSearch
          optionFilterProp="label"
          placeholder="Select Fleet Manager"
          value={selectedCompany}
          onChange={(value) => setSelectedCompany(value ?? null)}
          options={companyOptions}
          loading={companiesLoading}
          className="fuel-cards-page__company-select"
        />
      </div>

      <GenericTable title="Fuel Cards" dataSource={cards} columns={columns} />

      <Modal
        open={generateModalOpen}
        onCancel={closeGenerateModal}
        footer={null}
        centered
        className="generate-cards-modal"
      >
        <div className="top-gradient"></div>
        <div className="generate-cards-modal__body">
          <div className="generate-cards-modal__header">
            <div className="generate-cards-modal__icon-wrapper">
              <CreditCardOutlined className="generate-cards-modal__icon" />
            </div>
            <div className="generate-cards-modal__title-container">
              <div className="generate-cards-modal__title">
                Generate Fuel Cards
              </div>
              <div className="generate-cards-modal__subtitle">
                Select a fleet manager to generate its fuel cards.
              </div>
            </div>
          </div>

          <label className="generate-cards-modal__label">Fleet Manager</label>
          <Select
            showSearch
            optionFilterProp="label"
            placeholder="Select Fleet Manager"
            className="generate-cards-modal__input"
            value={generateCompanyId}
            onChange={setGenerateCompanyId}
            options={companyOptions}
            loading={companiesLoading}
          />

          {generateCompany && (
            <div className="generate-cards-modal__preview">
              <div className="generate-cards-modal__preview-row">
                <span>Vehicle Limit</span>
                <span>{generateCompany.vehicle_limit ?? 0}</span>
              </div>
              <div className="generate-cards-modal__preview-row">
                <span>Existing Cards</span>
                <span>{generateCompany.existing_cards ?? 0}</span>
              </div>
              <div className="generate-cards-modal__preview-row generate-cards-modal__preview-row--total">
                <span>Cards to Generate</span>
                <span>{cardsToGenerate}</span>
              </div>
              {cardsToGenerate === 0 && (
                <div className="generate-cards-modal__preview-note">
                  Fuel cards already exist for this company's vehicle limit.
                </div>
              )}
            </div>
          )}

          <div className="generate-cards-modal__buttons">
            <Button
              className="generate-cards-modal__btn-cancel"
              onClick={closeGenerateModal}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              className="generate-cards-modal__btn-confirm"
              loading={generating}
              disabled={!generateCompanyId || cardsToGenerate === 0}
              onClick={handleGenerateCards}
            >
              Generate
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FuelCards;
